import camelCase from "lodash-es/camelCase.js";
import forOwn from "lodash-es/forOwn.js";
import get from "lodash-es/get.js";
import groupBy from "lodash-es/groupBy.js";
import isArray from "lodash-es/isArray.js";
import isEqual from "lodash-es/isEqual.js";
import isPlainObject from "lodash-es/isPlainObject.js";
import set from "lodash-es/set.js";
import uniq from "lodash-es/uniq.js";
import uniqWith from "lodash-es/uniqWith.js";
export default class Parser {
    errorMessages;
    parsedMessagesStack = [];
    products = [];
    constructor(errorMessages) {
        this.errorMessages = errorMessages;
    }
    getMessagesFlatten(data, fallbackMessage, products) {
        const messages = this.getMessages(data, fallbackMessage, products).map(({ message }) => message);
        return uniq(messages);
    }
    // will remove additional data and unique messages
    getMessagesSimplified(data, fallbackMessage, products) {
        const messages = this.getMessages(data, fallbackMessage, products).map(({ message, path }) => ({ message, path }));
        return uniqWith(messages, isEqual);
    }
    getMessages(data, fallbackMessage, products) {
        if (!data)
            return [];
        if (fallbackMessage) {
            this.errorMessages.unknown = fallbackMessage;
        }
        this.products = products || [];
        const uniqueMessages = uniqWith(this.parseMessages(data), isEqual);
        return this.enrichProductError(uniqueMessages);
    }
    unpackPath(messages) {
        const objectStructure = {};
        messages.forEach((message) => {
            set(objectStructure, message.path, [
                ...get(objectStructure, message.path, []),
                message.message,
            ]);
        });
        return objectStructure;
    }
    unpackUniquePath(messages) {
        const objectStructure = {};
        const usedPaths = [];
        messages.forEach((message) => {
            const path = JSON.stringify(message.path);
            if (usedPaths.includes(path))
                return;
            usedPaths.push(path);
            set(objectStructure, message.path, message.message);
        });
        return objectStructure;
    }
    /**
     * unpackPathPreserveParsedMessage
     * will overwrite errors for the same path
     */
    unpackPathWithMessage(messages) {
        const objectStructure = {};
        messages.forEach((message) => {
            set(objectStructure, message.path, message);
        });
        return objectStructure;
    }
    getErrorMessage(errorName, path) {
        // specific error { a: { b: { c: 'message' } } }
        const message = get(this.errorMessages, [...path, camelCase(errorName)]);
        if (message)
            return message;
        // unspecific error { b: { c: 'message' } }
        const last = path.pop() || "";
        return get(this.errorMessages, [last, camelCase(errorName)], this.errorMessages.unknown);
    }
    mapFieldErrors(path, errorData) {
        const messages = [];
        errorData.forEach((data) => {
            if (!data?.error)
                return;
            const message = this.getErrorMessage(data.error, [...path]);
            if (!message)
                return;
            messages.push({
                ...data,
                path,
                message,
            });
        });
        return messages;
    }
    parseErrorsRecursive(errorData, path = []) {
        forOwn(errorData, (fieldErrorData, field) => {
            const fieldPath = [...path, camelCase(field)];
            if (isArray(fieldErrorData)) {
                this.mapFieldErrors(fieldPath, fieldErrorData).forEach((message) => {
                    this.parsedMessagesStack.push(message);
                });
                return;
            }
            if (isPlainObject(fieldErrorData)) {
                this.parseErrorsRecursive(fieldErrorData, fieldPath);
            }
        });
    }
    /**
     * Group and enrich errors with "articleNumber"-field with the product name.
     */
    enrichProductError(errors) {
        const errorsWithoutReason = errors.filter((error) => !error?.articleNumber);
        const messages = [...errorsWithoutReason];
        const errorsWithReason = groupBy(errors.filter((error) => !!error?.articleNumber), (entry) => entry.error);
        forOwn(errorsWithReason, (groupedErrors) => {
            messages.push({
                ...groupedErrors[0],
                message: this.formatErrorMessagesWithProducts(groupedErrors),
            });
        });
        return messages;
    }
    parseMessages(data) {
        this.parsedMessagesStack = [];
        this.parseErrorsRecursive(data);
        return this.parsedMessagesStack;
    }
    getProductByNumber(articleNumber) {
        return this.products?.find((product) => product.articleNumber === articleNumber);
    }
    formatErrorMessagesWithProducts(errors) {
        const names = errors.map((error) => this.getProductByNumber(error.articleNumber || "")?.name ||
            error.articleNumber);
        return `${errors[0].message}<br>"${names.join('", "')}".`;
    }
}
